import React from 'react';
import {
  XAxis,
  YAxis,
  VerticalGridLines,
  HorizontalGridLines,
  AreaSeries,
  FlexibleXYPlot,
  MarkSeries,
  LineSeries,
  LineMarkSeries,
  VerticalRectSeries,
} from 'react-vis';
import { Stack, Text } from '@fluentui/react';
import structuredClone from 'core-js/stable/structured-clone';
import ChartDefaults from '../../shared/ChartDefaults';
import Theme from '../../shared/Theme';
import Utils from '../../shared/Utils';
import Store from '../../shared/state/Store';
import DataReducer from '../../shared/state/DataReducer';
import Tooltip from '../../shared/Tooltip';
import StackTokens from '../../shared/StackTokens';

const xAxisStyle = {
  text: {
    fill: ChartDefaults.ThemeColors.AxisTicks,
    fontSize: '10px',
  },
  line: { stroke: ChartDefaults.ThemeColors.AxisLine },
};

const yAxisStyle = {
  text: {
    fill: ChartDefaults.ThemeColors.AxisTicks,
    fontSize: '10px',
  },
  line: { stroke: ChartDefaults.ThemeColors.AxisLine },
};

export default ({
  baselineData, userData, config, generalConfig, chartRef, className,
}) => {
  const isMountedRef = React.useRef(true);
  React.useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  const [globalState] = Store.useStore();
  const integerOffset = (config.nodeType && config.nodeType === 'IntegerInterval') ? 1 : 0;

  let minX = Number.MAX_VALUE;
  let maxX = -Number.MAX_VALUE;

  let maxY = 0;

  const infiniteIndexMapLower = {};
  const infiniteIndexMapUpper = {};

  const formatData = (dataSet, dataSetIndex) => {
    if (!dataSet || dataSet.length === 0) {
      return [];
    }
    const formattedPoints = dataSet.map((result, index) => {
      const label = `${result?.label ?? ''}`;
      const isInterval = label.indexOf(' - ') >= 0;
      const rangeSplit = label.split(' - ');
      let lower = parseFloat(rangeSplit[0]);
      let upper = parseFloat(rangeSplit[1]);
      let infiniteLower = false;
      let infiniteUpper = false;

      if (!isInterval) {
        upper = lower;
      }

      if (!Number.isFinite(lower)) {
        lower = -Number.MAX_VALUE;
        infiniteIndexMapLower[dataSetIndex] = infiniteIndexMapLower[dataSetIndex] || [];
        infiniteLower = true;
      } else {
        minX = Math.min(minX, lower);
      }

      if (!Number.isFinite(upper)) {
        upper = Number.MAX_VALUE;
        infiniteIndexMapUpper[dataSetIndex] = infiniteIndexMapUpper[dataSetIndex] || [];
        infiniteUpper = true;
      } else {
        maxX = Math.max(maxX, upper);
      }

      let midPoint;
      if (isInterval) {
        if (index === 0 && dataSet.length > 1) {
          midPoint = lower;
        } else if (index === dataSet.length - 1 && dataSet.length > 1) {
          midPoint = upper;
        } else {
          midPoint = (lower + upper) / 2;
        }
      } else {
        midPoint = lower;
      }

      const range = (upper - lower) + integerOffset;

      const x = midPoint;

      let { value } = result;
      if (!Number.isFinite(value)) {
        value = Number.MAX_VALUE * (value < 0 ? -1 : 1);
      }

      const y = (range > 0) ? value / range : value;

      maxY = Math.max(maxY, y);

      if (config.chartType === ChartDefaults.ChartTypes.Histogram) {
        const dataPoint = {
          x0: lower,
          x: upper + integerOffset,
          y0: 0,
          y,
          index,
        };
        if (infiniteLower) {
          infiniteIndexMapLower[dataSetIndex].push(dataPoint);
        }
        if (infiniteUpper) {
          infiniteIndexMapUpper[dataSetIndex].push(dataPoint);
        }
        return [dataPoint];
      }
      if (Math.abs(upper - lower) < Number.EPSILON) {
        const points = [
          { x: lower, y: 0 },
          { x: lower, y },
        ];
        if (infiniteLower) {
          infiniteIndexMapLower[dataSetIndex].push(...points);
        }
        return points;
      }
      if (isInterval && dataSet.length === 1) {
        const pointsLower = [
          { x: lower, y: 0 },
          { x: lower, y },
        ];
        const pointsUpper = [
          { x: upper, y },
          { x: upper, y: 0 },
        ];
        if (infiniteLower) {
          infiniteIndexMapLower[dataSetIndex].push(...pointsLower);
        }
        if (infiniteUpper) {
          infiniteIndexMapUpper[dataSetIndex].push(...pointsUpper);
        }
        return [...pointsLower, ...pointsUpper];
      }

      const point = { x, y };
      if (infiniteLower) {
        infiniteIndexMapLower[dataSetIndex].push(point);
      }
      if (infiniteUpper) {
        infiniteIndexMapUpper[dataSetIndex].push(point);
      }
      return [point];
    }).flat(1);

    return formattedPoints;
  };

  let datasetIndex = 0;
  const baselineChartData = baselineData
    ? formatData(structuredClone(baselineData), datasetIndex += 1)
    : [];
  const userChartData = userData
    ? formatData(structuredClone(userData), datasetIndex += 1)
    : [];

  let { curveFunctionName } = config;
  if (!curveFunctionName) {
    curveFunctionName = (config.nodeType && config.nodeType === 'IntegerInterval')
      ? ChartDefaults.CurveInteger
      : ChartDefaults.CurveContinuous;
  }

  const baselineProps = {
    key: 'baseline',
    curve: ChartDefaults.CurveFunctions[curveFunctionName],
    data: baselineChartData,
    color: generalConfig.colorBaseline || ChartDefaults.ColorBaseline,
  };

  const userProps = {
    key: 'user',
    curve: ChartDefaults.CurveFunctions[curveFunctionName],
    data: userChartData,
    color: generalConfig.colorUser || ChartDefaults.ColorUser,
    opacity: 0.9,
  };

  let chartContent = '';
  if (config.chartType === ChartDefaults.ChartTypes.Area) {
    chartContent = ([
      <AreaSeries
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...baselineProps}
      />,
      <AreaSeries
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...userProps}
      />,
    ]);
  }

  if (config.chartType === ChartDefaults.ChartTypes.Histogram) {
    baselineProps.stroke = Theme.Colors.Blackish;
    userProps.stroke = Theme.Colors.Blackish;
    chartContent = ([
      <VerticalRectSeries
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...baselineProps}
      />,
      <VerticalRectSeries
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...userProps}
      />,
    ]);
  }
  if (config.chartType === ChartDefaults.ChartTypes.Scatter) {
    baselineProps.size = 3;
    userProps.size = 3;
    chartContent = ([
      <MarkSeries
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...baselineProps}
      />,
      <MarkSeries
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...userProps}
      />,
    ]);
  }

  if (config.chartType === ChartDefaults.ChartTypes.Line) {
    baselineProps.style = { fill: 'none' };
    baselineProps.strokeWidth = '2px';
    userProps.strokeWidth = '2px';
    userProps.style = { fill: 'none' };
    userProps.opacity = 1;
    chartContent = ([
      <LineSeries
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...baselineProps}
      />,
      <LineSeries
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...userProps}
      />,
    ]);
  }

  if (config.chartType === ChartDefaults.ChartTypes.ScatterLine) {
    baselineProps.style = { fill: 'none' };
    baselineProps.strokeWidth = '2px';
    userProps.strokeWidth = '2px';
    userProps.style = { fill: 'none' };
    userProps.opacity = 1;
    baselineProps.size = 3;
    userProps.size = 3;
    chartContent = ([
      <LineMarkSeries
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...baselineProps}
      />,
      <LineMarkSeries
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...userProps}
      />,
    ]);
  }

  const hasFiniteXExtent = Number.isFinite(minX) && Number.isFinite(maxX) && minX <= maxX;
  const rangeX = hasFiniteXExtent ? Math.abs(maxX - minX) : 0;
  let paddingX = 1;
  if (rangeX > 0) {
    paddingX = rangeX * 0.2;
  } else if (hasFiniteXExtent) {
    paddingX = Math.abs(minX) * 0.2;
  }
  let xDomainMin = hasFiniteXExtent ? minX - paddingX : 0;
  let xDomainMax = hasFiniteXExtent ? maxX + paddingX : 1;

  const infinitePadding = paddingX * ChartDefaults.InfinitePaddingFactor;

  if (infiniteIndexMapLower && Object.keys(infiniteIndexMapLower).length > 0) {
    xDomainMin -= paddingX;
    Object.keys(infiniteIndexMapLower).forEach((dsIndex) => {
      infiniteIndexMapLower[dsIndex].forEach((point) => {
        if (point.x0 !== undefined) {
          // eslint-disable-next-line no-param-reassign
          point.x0 = xDomainMin;
        } else {
          // eslint-disable-next-line no-param-reassign
          point.x = xDomainMin;
        }
      });
    });
    xDomainMin -= infinitePadding;
  }
  if (infiniteIndexMapUpper && Object.keys(infiniteIndexMapUpper).length > 0) {
    xDomainMax += paddingX;
    Object.keys(infiniteIndexMapUpper).forEach((dsIndex) => {
      infiniteIndexMapUpper[dsIndex].forEach((point) => {
        // eslint-disable-next-line no-param-reassign
        point.x = xDomainMax;
      });
    });
    xDomainMax += infinitePadding;
  }

  if (!Number.isFinite(xDomainMin)) {
    xDomainMin = -Number.MAX_VALUE;
  }
  if (!Number.isFinite(xDomainMax)) {
    xDomainMax = Number.MAX_VALUE;
  }
  const xDomain = [xDomainMin, xDomainMax];

  const edgeTolerance = (xDomainMax - xDomainMin) * 0.01;
  const xAxisTickFormat = (value) => {
    if (Object.keys(infiniteIndexMapLower).length > 0 && Math.abs(value - xDomainMin) <= edgeTolerance) return '-∞';
    if (Object.keys(infiniteIndexMapUpper).length > 0 && Math.abs(value - xDomainMax) <= edgeTolerance) return '∞';

    if (value === 0) return '0';

    const abs = Math.abs(value);

    if (abs >= 1e6 || abs < 1e-6) {
      return value.toExponential(2)
        .replace('e+', 'e')
        .replace('e0', '');
    }

    return value.toLocaleString(undefined, {
      maximumFractionDigits: 6,
    });
  };

  let yDomainMax = maxY;
  if (!Number.isFinite(yDomainMax)) {
    yDomainMax = Number.MAX_VALUE;
  }
  const yDomain = [0, yDomainMax];

  const [tooltipData, setTooltipData] = React.useState(null);
  const [mousePoint, setMousePoint] = React.useState({ x: 0, y: 0 });
  const [mousePointHit, setMousePointHit] = React.useState({ x: 0, y: 0 });
  React.useEffect(() => {
    if (tooltipData === null) {
      return;
    }
    if (Math.abs(mousePointHit.x - mousePoint.x) > 50 || Math.abs(mousePointHit.y - mousePoint.y) > 100) {
      setTooltipData(null);
    }
  }, [mousePoint]);

  const linesToPlot = [];
  const plotExtraItemsRequired = config.plotPercentiles || config.plotMean || config.plotMedian;

  if (plotExtraItemsRequired) {
    [
      DataReducer.getCachedResultsForDataSet(globalState, DataReducer.DefaultCaseNames.BASELINE),
      DataReducer.getCachedResultsForDataSet(globalState, DataReducer.DefaultCaseNames.USER),
    ].forEach((dsCalculationData, dsIndex) => {
      if (!dsCalculationData) {
        return;
      }

      const key = Utils.createNetworkNodeKey(config.network, config.node);

      const resultData = dsCalculationData[key];

      if (!resultData) {
        return;
      }

      let dsKey;
      let dsColor;
      if (dsIndex === 0) {
        dsKey = 'baseline';
        dsColor = generalConfig.colorBaseline || ChartDefaults.ColorBaseline;
      } else {
        dsKey = 'user';
        dsColor = generalConfig.colorUser || ChartDefaults.ColorUser;
      }

      const generatePlotData = (uKey, dValue, prefixText) => ({
        key: uKey,
        color: dsColor,
        strokeWidth: '1px',
        style: {
          filter: 'drop-shadow(#e4e4e4 1px 0px 1px )',
        },
        data: [
          { x: dValue, y: 0 },
          { x: dValue, y: maxY },
        ],
        onSeriesMouseOver: () => {
          setTooltipData(null);
          setTimeout(() => {
            setMousePointHit(mousePoint);
            setTooltipData({
              color: dsColor,
              dsKey: (dsIndex === 0) ? DataReducer.DefaultCaseNames.BASELINE : DataReducer.DefaultCaseNames.USER,
              text: prefixText,
              value: Utils.roundFormat(dValue, config.decimalPlaces, ChartDefaults.decimalPlaces),
            });
          }, 1);
        },
      });

      if (config.plotPercentiles) {
        if (config.customPercentiles && config.customPercentiles.length > 0) {
          if (resultData.percentiles) {
            Object.values(config.customPercentiles).forEach((pName) => {
              if (!resultData.percentiles[pName]) {
                return;
              }
              linesToPlot.push(generatePlotData(dsKey + pName, resultData.percentiles[pName], `${pName}th percentile`));
            });
          }
        } else if (resultData.summaryStatistics) {
          linesToPlot.push(generatePlotData(`${dsKey}lowerPercentile`, resultData.summaryStatistics.lowerPercentile, 'Lower percentile'));
          linesToPlot.push(generatePlotData(`${dsKey}upperPercentile`, resultData.summaryStatistics.upperPercentile, 'Upper percentile'));
        }
      }

      if (config.plotMean && resultData.summaryStatistics.mean) {
        linesToPlot.push(generatePlotData(`${dsKey}mean`, resultData.summaryStatistics.mean, 'Mean'));
      }

      if (config.plotMedian && resultData.summaryStatistics.median) {
        linesToPlot.push(generatePlotData(`${dsKey}median`, resultData.summaryStatistics.median, 'Median'));
      }
    });
  }

  const margins = {};
  ['Left', 'Right', 'Top', 'Bottom'].forEach((p) => {
    const marginKey1 = `padding${p}`;
    if (config[marginKey1]) {
      margins[p.toLowerCase()] = parseInt(config[marginKey1], 10);
    }
  });

  const [paddingBottomAuto, setPaddingBottomAuto] = React.useState(0);
  const [paddingLeftAuto, setPaddingLeftAuto] = React.useState(0);

  const adjustMargins = (svg) => {
    if (!isMountedRef.current) {
      return;
    }
    if (!svg) {
      return;
    }

    const xAxis = svg.getElementsByClassName('rv-xy-plot__axis--horizontal')[0];
    const yAxis = svg.getElementsByClassName('rv-xy-plot__axis--vertical')[0];

    if (!xAxis || !yAxis) {
      return;
    }

    const xAxisLeftBeyondYAxis = yAxis.getBoundingClientRect().x - xAxis.getBoundingClientRect().x;
    setPaddingLeftAuto(Math.max(xAxisLeftBeyondYAxis, 0) + yAxis.getBoundingClientRect().width);

    if (xAxis) {
      const axisRect = xAxis.getBoundingClientRect();
      setPaddingBottomAuto(axisRect.height);
    }
  };

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!isMountedRef.current) {
        return;
      }
      if ((baselineData && baselineData.length > 0) || (userData && userData.length > 0)) {
        const svg = Utils.getSvg(chartRef);
        if (svg) {
          adjustMargins(svg);
        }
      }
    }, 10);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [baselineData, userData, config]);

  margins.bottom = margins.bottom !== undefined ? margins.bottom : paddingBottomAuto;
  margins.left = margins.left !== undefined ? margins.left : paddingLeftAuto;

  const [forceRender, setForceRender] = React.useState(false);
  React.useEffect(() => {
    if (!config.height) {
      setForceRender(true);
      const timeoutId = setTimeout(() => {
        if (isMountedRef.current) {
          setForceRender(false);
        }
      }, 10);
      return () => {
        clearTimeout(timeoutId);
      };
    }
    setForceRender(false);
    return undefined;
  }, [config.height]);

  return (
    <div
      style={{
        width: '100%',
        height: forceRender ? `${ChartDefaults.CHART_HEIGHT}px` : (config.height || '100%'),
        minHeight: (config.height === undefined) ? `${ChartDefaults.CHART_HEIGHT}px` : 'auto',
      }}
      ref={chartRef}
      className={className}
    >
      <FlexibleXYPlot
        margin={margins}
        xDomain={xDomain}
        yDomain={yDomain}
        style={{
          overflow: 'hidden',
          backgroundColor: 'white',
          fontFamily: ChartDefaults.ContinuousChartStyleOptions.fontFamily,
        }}
        onMouseMove={(event) => {
          setMousePoint({ x: event.clientX, y: event.clientY });
        }}
      >
        <VerticalGridLines />
        <HorizontalGridLines />
        <XAxis
          style={xAxisStyle}
          tickLabelAngle={-45}
          tickSizeOuter={5}
          tickSizeInner={0}
          tickFormat={xAxisTickFormat}
        />
        <YAxis
          style={yAxisStyle}
          tickSizeOuter={5}
          tickSizeInner={0}
        />

        {chartContent.map((chart) => chart)}

        {linesToPlot.map((pData) => (
          <LineSeries
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...pData}
          />
        ))}
      </FlexibleXYPlot>

      {tooltipData !== null && (
        <Tooltip
          x={mousePoint.x}
          y={mousePoint.y}
        >
          <Stack horizontal tokens={StackTokens.spacing}>
            <div
              style={{
                width: '20px',
                height: '20px',
                background: tooltipData.color,
              }}
            />
            <Text>{tooltipData.dsKey}</Text>
            <Text>{`${tooltipData.text}: ${tooltipData.value}`}</Text>
          </Stack>
        </Tooltip>
      )}

    </div>
  );
};
